import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getV2ProviderLabsAvailable: build.query<
      GetV2ProviderLabsAvailableApiResponse,
      GetV2ProviderLabsAvailableApiArg
    >({
      query: () => ({ url: `/v2/provider/labs/available` }),
    }),
    postV2ProviderLabsBundles: build.mutation<
      PostV2ProviderLabsBundlesApiResponse,
      PostV2ProviderLabsBundlesApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/labs/bundles`,
        method: "POST",
        body: queryArg.createCustomBundleRequest,
      }),
    }),
    deleteV2ProviderLabsBundlesByBundleId: build.mutation<
      DeleteV2ProviderLabsBundlesByBundleIdApiResponse,
      DeleteV2ProviderLabsBundlesByBundleIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/labs/bundles/${queryArg.bundleId}`,
        method: "DELETE",
      }),
    }),
    postV2ProviderLabsOrders: build.mutation<
      PostV2ProviderLabsOrdersApiResponse,
      PostV2ProviderLabsOrdersApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/labs/orders`,
        method: "POST",
        body: queryArg.createLabOrderRequest,
      }),
    }),
    postV2ProviderLabsOrdersGeneratePdf: build.mutation<
      PostV2ProviderLabsOrdersGeneratePdfApiResponse,
      PostV2ProviderLabsOrdersGeneratePdfApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/labs/orders/generate/pdf`,
        method: "POST",
        body: queryArg.createLabOrderRequest,
      }),
    }),
    postV2ProviderLabsRecommendations: build.mutation<
      PostV2ProviderLabsRecommendationsApiResponse,
      PostV2ProviderLabsRecommendationsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/labs/recommendations`,
        method: "POST",
        body: queryArg.startLabRecommendationRequest,
      }),
    }),
    getV2ProviderLabsRecommendationsByTaskId: build.query<
      GetV2ProviderLabsRecommendationsByTaskIdApiResponse,
      GetV2ProviderLabsRecommendationsByTaskIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/labs/recommendations/${queryArg.taskId}`,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetV2ProviderLabsAvailableApiResponse =
  /** status 200 OK */ LabsStructuredResponse;
export type GetV2ProviderLabsAvailableApiArg = void;
export type PostV2ProviderLabsBundlesApiResponse =
  /** status 201 Created */ CustomBundleResponse;
export type PostV2ProviderLabsBundlesApiArg = {
  createCustomBundleRequest: CreateCustomBundleRequest;
};
export type DeleteV2ProviderLabsBundlesByBundleIdApiResponse =
  /** status 200 OK */ DeleteBundleResponse;
export type DeleteV2ProviderLabsBundlesByBundleIdApiArg = {
  bundleId: string;
};
export type PostV2ProviderLabsOrdersApiResponse =
  /** status 201 Created */ LabOrderResponse;
export type PostV2ProviderLabsOrdersApiArg = {
  createLabOrderRequest: CreateLabOrderRequest;
};
export type PostV2ProviderLabsOrdersGeneratePdfApiResponse =
  /** status 200 OK */ LabOrderPdfGenerateResponse;
export type PostV2ProviderLabsOrdersGeneratePdfApiArg = {
  createLabOrderRequest: CreateLabOrderRequest;
};
export type PostV2ProviderLabsRecommendationsApiResponse =
  /** status 200 OK */ StartLabRecommendationResponse;
export type PostV2ProviderLabsRecommendationsApiArg = {
  startLabRecommendationRequest: StartLabRecommendationRequest;
};
export type GetV2ProviderLabsRecommendationsByTaskIdApiResponse =
  /** status 200 OK */ LabRecommendationStatusResponse;
export type GetV2ProviderLabsRecommendationsByTaskIdApiArg = {
  taskId: string;
};
export type LabBundle = {
  /** Unique identifier for the lab bundle */
  bundle_id: string;
  /** Description of what the bundle includes */
  description: string;
  /** Whether this is a custom user-created bundle */
  is_custom?: boolean;
  /** Display name of the lab bundle */
  name: string;
  /** Special instructions for the bundle */
  special_instructions: string;
  /** List of test IDs included in this bundle (system bundles) */
  test_ids?: string[] | null;
  /** List of test names included in this bundle (custom bundles) */
  test_names?: string[] | null;
};
export type LabTest = {
  /** Display name of the lab test */
  name: string;
  /** Purpose and description of the lab test */
  purpose: string;
  /** Unique identifier for the lab test */
  test_id: string;
};
export type LabsStructuredResponse = {
  /** List of available lab bundles */
  lab_bundles: LabBundle[];
  /** List of available lab tests with details */
  lab_tests: LabTest[];
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type ServerErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the server error */
  status?: number;
};
export type CustomBundleResponse = {
  /** Unique identifier for the bundle */
  bundle_id: string;
  /** Bundle creation timestamp */
  created_at: string;
  /** Description of the bundle */
  description?: string | null;
  /** Name of the custom lab bundle */
  name: string;
  /** Special instructions for the bundle */
  special_instructions?: string | null;
  /** List of lab test names */
  test_names: string[];
  /** Bundle update timestamp */
  updated_at?: string | null;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type CreateCustomBundleRequest = {
  /** Description of the custom lab bundle */
  description?: string | null;
  /** Name of the custom lab bundle */
  name: string;
  /** Special instructions for the bundle */
  special_instructions?: string | null;
  /** List of lab test names (at least one required) */
  test_names: string[];
};
export type DeleteBundleResponse = {
  /** Success message */
  message: string;
};
export type LabOrderResponse = {
  /** Order creation timestamp */
  created_at: string;
  /** List of ICD-10 diagnosis codes */
  icd_10_codes?: string[] | null;
  /** Ordered lab test names */
  lab_tests: string[];
  /** Patient ID */
  patient_id: string;
  /** Special instructions for the lab order */
  special_instructions?: string | null;
};
export type CreateLabOrderRequest = {
  /** List of ICD-10 diagnosis codes */
  icd_10_codes?: string[] | null;
  /** List of lab test names (at least one required) */
  lab_tests: string[];
  /** Patient for whom the order is created */
  patient_id: string;
  /** Whether to include provider name in signature */
  sign_with_name?: boolean;
  /** Special instructions for the lab order */
  special_instructions?: string | null;
};
export type LabOrderPdfGenerateResponse = {
  /** Suggested filename for the PDF */
  filename: string;
  /** Base64 encoded PDF data */
  pdf_data: string;
};
export type StartLabRecommendationResponse = {
  /** Task ID for tracking workflow progress */
  task_id: string;
};
export type StartLabRecommendationRequest = {
  /** ID of the clinical note to analyze. If not provided, the most recent note for the patient will be used */
  note_id?: string | null;
  /** Patient ID for whom to recommend labs */
  patient_id: string;
};
export type LabRecommendationStatusResponse = {
  /** Error message if workflow failed */
  error?: string | null;
  /** Lab order data if workflow completed */
  lab_order?: {
    [key: string]: any;
  } | null;
  /** Status of the workflow (completed, pending, failed, error) */
  status: string;
};
